import React, { useMemo } from 'react'
import { useQuizViewer } from '../hooks/useQuizViewer'
import QuizHeader from '../../components/QuizHeader'
import PageRenderer from '../../components/PageRenderer'
import { ElementType, QuizConfig } from '../../types'
import { getQuestionId } from '../lib/supabase'
import { logger } from '../lib/logger'
import SEOUpdater from './SEOUpdater'
import ScorePopup from './ScorePopup'

// Default config to fallback to
const DEFAULT_CONFIG: QuizConfig = {
  colors: {
    primary: '#3b82f6',
    background: '#ffffff',
    text: '#1e293b',
    title: '#0f172a'
  },
  fontFamily: 'Inter',
  fontSizes: {
    title: 'text-2xl',
    subtitle: 'text-base',
    option: 'text-sm'
  },
  borderRadius: 'medium',
  contentWidth: 'medium',
  contentAlignment: 'center',
  containerStyle: 'flat',
  header: {
    showStepIndicator: true,
    showBackButton: true,
    progressBarStyle: 'default',
    logoPosition: 'center'
  },
  gamification: {
    coinsEnabled: false,
    style: 'coins',
    sound: 'default'
  },
  validation: {
    requiredMessage: 'Por favor, preencha este campo'
  },
  scripts: {
    head: '',
    footer: ''
  }
}

// Helper to load fonts
const loadFont = async (fontFamily: string) => {
  if (!fontFamily) return
  const link = document.createElement('link')
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}:wght@400;500;600;700&display=swap`
  link.rel = 'stylesheet'
  document.head.appendChild(link)
}

const QuizViewer: React.FC = () => {
  logger.log('🎨 [QuizViewer] Componente renderizado')

  const [formValues, setFormValues] = React.useState<Record<string, any>>({})
  const [isRedirecting, setIsRedirecting] = React.useState(false)
  const isRedirectingRef = React.useRef(false)
  const quizCompletedRef = React.useRef(false)
  const [confettiOn, setConfettiOn] = React.useState(false)
  const confettiTimeoutsRef = React.useRef<number[]>([])
  const [confettiKey, setConfettiKey] = React.useState(0)

  // Gamification states
  const [coins, setCoins] = React.useState(0)
  const [coinAnimOn, setCoinAnimOn] = React.useState(false)
  const [coinAward, setCoinAward] = React.useState(0)
  const [coinDx, setCoinDx] = React.useState(0)
  const [coinDy, setCoinDy] = React.useState(0)
  const [coinKey, setCoinKey] = React.useState(0)
  const [awardedSteps, setAwardedSteps] = React.useState<Set<number>>(new Set())
  const [showScorePopup, setShowScorePopup] = React.useState(false)
  const [popupContent, setPopupContent] = React.useState({ text: '', footer: '' })
  const [validationError, setValidationError] = React.useState<string | null>(null)
  const [invalidFields, setInvalidFields] = React.useState<string[]>([])

  // Estado para controlar checkboxes dos elementos de preço
  const [priceCheckboxes, setPriceCheckboxes] = React.useState<Record<string, boolean>>({})

  // Resetar estados de bloqueio quando a página ganha foco novamente
  React.useEffect(() => {
    const resetButtonStates = () => {
      const buttons = document.querySelectorAll('button')
      buttons.forEach(button => {
        button.disabled = false
        button.style.opacity = ''
        button.style.cursor = ''
        button.style.pointerEvents = ''
      })

      const links = document.querySelectorAll('a')
      links.forEach(link => {
        link.style.opacity = ''
        link.style.cursor = ''
        link.style.pointerEvents = ''
      })

      setIsRedirecting(false)
      isRedirectingRef.current = false
    }

    window.addEventListener('focus', resetButtonStates)
    window.addEventListener('pageshow', resetButtonStates)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        resetButtonStates()
      }
    })

    return () => {
      window.removeEventListener('focus', resetButtonStates)
      window.removeEventListener('pageshow', resetButtonStates)
      document.removeEventListener('visibilitychange', resetButtonStates)
    }
  }, [])

  const {
    quiz,
    currentStep,
    answers,
    submitAnswer,
    nextStep,
    prevStep,
    completeQuiz,
    submitLead,
    loading,
    error,
    trackExternalClick,
    addUTMsToRedirectUrl,
    score: savedScore,
    awardedSteps: savedAwardedSteps,
    saveScore,
    leadData,
    isRecoveredCompleted
  } = useQuizViewer()

  // Sync local state with saved session state (Gamification)
  React.useEffect(() => {
    if (savedScore > 0 && coins === 0) {
      setCoins(savedScore)
    }
    if (savedAwardedSteps && savedAwardedSteps.length > 0 && awardedSteps.size === 0) {
      setAwardedSteps(new Set(savedAwardedSteps))
    }
  }, [savedScore, savedAwardedSteps])

  // Sync saved leads and answers into formValues for Inputs & Interpolation
  React.useEffect(() => {
    // Combine answers and leadData to restore input states
    const restoredValues: Record<string, any> = {};
    let hasData = false;

    logger.log('🔄 [QuizViewer] Data Sync Check:', {
      hasLeadData: !!leadData,
      leadKeys: leadData ? Object.keys(leadData) : [],
      hasAnswers: !!answers,
      answerKeys: answers ? Object.keys(answers) : []
    });

    // 1. Restore from leads (Variables/Forms)
    if (leadData && Object.keys(leadData).length > 0) {
      Object.keys(leadData).forEach(key => {
        const item = leadData[key];
        if (item && typeof item === 'object' && item.value !== undefined) {
          // Use unified structure
          restoredValues[key] = item;
          // Also map by customId if available for direct access
          if (item.customId && item.customId !== key) {
            restoredValues[item.customId] = item;
          }
        } else {
          restoredValues[key] = item;
        }
        hasData = true;
      });
    }

    // 2. Restore from answers (Questions) - mostly for interpolation contexts
    if (answers && Object.keys(answers).length > 0) {
      Object.keys(answers).forEach(key => {
        const item = answers[key];
        // If it's an object with value, use it.
        // We do not want to overwrite Forms data if there's a collision on strict keys,
        // but typically question IDs differ from form variable names.
        if (restoredValues[key] === undefined) {
          restoredValues[key] = item;
          hasData = true;
          if (item && typeof item === 'object' && item.customId) {
            // If customId is set (variable name), expose it too
            if (restoredValues[item.customId] === undefined) {
              restoredValues[item.customId] = item;
            }
          }
        }
      });
    }

    if (hasData) {
      logger.log('🔄 [QuizViewer] Dados restaurados identificados:', restoredValues);
      setFormValues(prev => {
        // If we have local changes (prev), we generally respect them,
        // but if prev is empty (initial load), we populate.
        if (Object.keys(prev).length === 0) {
          logger.log('🔄 [QuizViewer] Restaurando formValues (inicial):', Object.keys(restoredValues).length, 'itens');
          return restoredValues;
        }
        // If we already have values, we merge ONLY missing ones to avoid overwriting user input?
        // Or do we trust the session more?
        // If the session was just loaded, prev might be empty.
        // If user is typing, prev has data.
        // Since this effect runs on [answers, leadData], which change on load or background save.
        // We should merge safely: keys that are NOT in prev.
        const next = { ...prev };
        let changed = false;
        Object.keys(restoredValues).forEach(k => {
          if (next[k] === undefined) { // Only fill if missing
            next[k] = restoredValues[k];
            changed = true;
            logger.log('🔄 [QuizViewer] Preenchendo valor faltante:', k, restoredValues[k]);
          }
        });
        if (changed) {
          logger.log('🔄 [QuizViewer] Inserindo dados restaurados faltantes em formValues');
          return next;
        }
        return prev;
      });
    } else {
      logger.log('⚠️ [QuizViewer] Nenhum dado para restaurar em formValues.');
    }
  }, [answers, leadData]); // Sync whenever session data loads/updates

  const saveLeads = async () => {
    if (Object.keys(formValues).length > 0) {
      await submitLead(formValues)
    }
  }

  const [activeConfettiType, setActiveConfettiType] = React.useState<'exit' | 'entry'>('exit')

  const triggerConfetti = (type: 'exit' | 'entry' = 'exit') => {
    setActiveConfettiType(type)
    setConfettiKey(prev => prev + 1)
    setConfettiOn(true)
  }

  // Trigger confetti on entry if configured
  React.useEffect(() => {
    const currentPage = quiz?.quiz_data?.pages?.[currentStep]
    if (currentPage?.confettiOnEntryConfig?.enabled) {
      const t = setTimeout(() => triggerConfetti('entry'), 100)
      return () => clearTimeout(t)
    }
  }, [currentStep, quiz])

  // Reset confetti and validation on step change
  React.useEffect(() => {
    window.scrollTo(0, 0)
    setConfettiOn(false)
    confettiTimeoutsRef.current.forEach(id => clearTimeout(id))
    confettiTimeoutsRef.current = []
    setInvalidFields([])
    setValidationError(null)
  }, [currentStep])

  // Save leads incrementally
  React.useEffect(() => {
    if (Object.keys(answers).length > 0 || Object.keys(formValues).length > 0) {
      saveLeads()
    }
  }, [answers, formValues])

  const config = useMemo(() => {
    if (!quiz?.quiz_data?.config) return DEFAULT_CONFIG
    const loadedConfig = quiz.quiz_data.config
    return {
      ...DEFAULT_CONFIG,
      ...loadedConfig,
      colors: { ...DEFAULT_CONFIG.colors, ...loadedConfig.colors },
      fontSizes: { ...DEFAULT_CONFIG.fontSizes, ...loadedConfig.fontSizes },
      header: { ...DEFAULT_CONFIG.header, ...loadedConfig.header },
      gamification: { ...DEFAULT_CONFIG.gamification, ...loadedConfig.gamification },
      validation: { ...DEFAULT_CONFIG.validation, ...loadedConfig.validation },
      scripts: { ...DEFAULT_CONFIG.scripts, ...loadedConfig.scripts },
    } as QuizConfig
  }, [quiz])

  const confettiParticles = React.useMemo(() => {
    if (!confettiOn) return null
    const currentPage = quiz?.quiz_data?.pages?.[currentStep]
    const confettiConfig = activeConfettiType === 'entry'
      ? currentPage?.confettiOnEntryConfig
      : currentPage?.confettiConfig
    const amountBase = confettiConfig?.amount === 'high' ? 100 : confettiConfig?.amount === 'low' ? 30 : 60
    const direction = confettiConfig?.direction || 'center'
    const amount = direction === 'sides' ? amountBase * 2.5 : amountBase
    const style = confettiConfig?.style || 'paper'
    const particles = Array.from({ length: amount }).map((_, i) => {
      let startX = '50%'
      let startY = '50%'
      let angle, dist
      if (direction === 'top') {
        startX = `${Math.random() * 100}%`
        startY = '-10%'
        angle = Math.PI / 2 + (Math.random() - 0.5) * 0.5
        dist = window.innerHeight + 100
      } else if (direction === 'sides') {
        const side = i % 2 === 0 ? 0 : 100
        startX = `${side}%`
        startY = `${60 + Math.random() * 30}%`
        if (side === 0) {
          angle = -0.2 - (Math.random() * 1.2)
        } else {
          angle = -Math.PI + 0.2 + (Math.random() * 1.2)
        }
        dist = confettiConfig?.amount === 'high' ? 600 + Math.random() * 400 : 400 + Math.random() * 300
      } else {
        angle = Math.random() * Math.PI * 2
        dist = confettiConfig?.amount === 'high' ? 200 + Math.random() * 350 : 120 + Math.random() * 220
      }
      let colors
      if (style === 'stars') {
        colors = ['#FFD700', '#FFC107', '#FFEB3B', '#F9A825', '#FFB300']
      } else if (style === 'fireworks') {
        colors = ['#FF1744', '#F50057', '#D500F9', '#651FFF', '#00E5FF', '#00E676', '#FFD600', '#FF6D00']
      } else {
        colors = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6']
      }
      const baseSize = style === 'stars' ? 16 : style === 'fireworks' ? 4 : 5
      const sizeVariation = style === 'stars' ? 14 : style === 'fireworks' ? 3 : 5
      const baseDuration = direction === 'sides' ? 2500 : (style === 'fireworks' ? 1000 : 700)
      const varDuration = direction === 'sides' ? 1000 : 500
      return {
        startX,
        startY,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        duration: baseDuration + Math.random() * varDuration,
        size: baseSize + Math.random() * sizeVariation,
        delay: Math.random() * 60,
        bg: colors[i % colors.length],
        startRot: Math.floor(Math.random() * 360),
        endRot: Math.floor(Math.random() * 360)
      }
    })
    return { particles, style }
  }, [confettiKey, quiz, currentStep, activeConfettiType])

  React.useEffect(() => {
    if (config.fontFamily && config.fontFamily !== 'Inter') {
      loadFont(config.fontFamily).catch(err => {
        logger.warn('⚠️ [QuizViewer] Erro ao carregar fonte, usando fallback:', err)
      })
    }
  }, [config.fontFamily])

  // Detectar chegada na última página e completar o quiz
  // ATENÇÃO: O trackPageView roda via useEffect em useQuizViewer quando currentStep muda
  // Precisamos dar tempo suficiente para o trackPageView da última página ser chamado e salvo
  // antes de chamar completeQuiz para evitar que a sessão seja marcada como "completed"
  // e o tracking seja ignorado
  const totalStepsForEffect = quiz?.quiz_data?.pages?.length || 0
  const isLastPageForEffect = currentStep >= totalStepsForEffect - 1 && totalStepsForEffect > 0

  React.useEffect(() => {
    if (isLastPageForEffect && !quizCompletedRef.current && quiz) {
      logger.log('🎉 [QuizViewer] Última página atingida, aguardando tracking e depois completando quiz...', { currentStep, totalSteps: totalStepsForEffect, formValues })
      quizCompletedRef.current = true

      // Delay maior para garantir que:
      // 1. O useEffect de trackPageView em useQuizViewer seja executado
      // 2. A requisição de savePageHistoryToDatabase seja enviada e processada
      // 3. A fila de salvamento seja processada completamente
      const completeAfterTracking = async () => {
        // Aguardar um tempo maior para garantir que o tracking foi salvo
        await new Promise(resolve => setTimeout(resolve, 1500))
        await saveLeads()
        await completeQuiz()
      }

      completeAfterTracking()
    }
  }, [isLastPageForEffect, currentStep, quiz])

  const playCoin = () => {
    const soundType = config.gamification?.sound || 'default'
    if (soundType === 'default') {
      try {
        const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext
        const ctx = new Ctx()
        const o = ctx.createOscillator()
        const g = ctx.createGain()
        o.type = 'triangle'
        o.frequency.setValueAtTime(700, ctx.currentTime)
        g.gain.setValueAtTime(0.001, ctx.currentTime)
        o.connect(g)
        g.connect(ctx.destination)
        o.start()
        o.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08)
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.03)
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28)
        o.stop(ctx.currentTime + 0.3)
      } catch { }
    } else {
      const audio = new Audio(`/sounds/${soundType}.mp3`)
      audio.volume = 0.6
      audio.play().catch(console.warn)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 5a7 7 0 100 14 7 7 0 000-14z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Erro ao carregar quiz</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  if (!quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 5a7 7 0 100 14 7 7 0 000-14z" />
            </svg>
          </div>
          <h1 className="text-5xl font-bold text-slate-300 mb-2">404</h1>
          <h2 className="text-xl font-bold text-slate-800 mb-3">Página não encontrada</h2>
          <p className="text-slate-500">
            A página que você está procurando não existe ou foi removida.
          </p>
        </div>
      </div>
    )
  }

  const renderCurrentPage = () => {
    const currentPageData = quiz?.quiz_data?.pages?.[currentStep]

    if (!currentPageData) return null

    const isMultipleChoice = currentPageData.elements?.some(
      (el: any) => el.type === ElementType.Options && el.multiple === true
    ) || false

    const getOptionTexts = (answer: string | string[]) => {
      if (!currentPageData.elements) return Array.isArray(answer) ? answer.join('; ') : answer
      const optionsElement = currentPageData.elements.find((el: any) => el.type === ElementType.Options)
      if (!optionsElement || !optionsElement.options) return Array.isArray(answer) ? answer.join('; ') : answer

      const ids = Array.isArray(answer) ? answer : [answer]
      const texts = ids.map(id => {
        const opt = optionsElement.options.find((o: any) => o.id === id)
        return opt ? opt.text : id
      })
      return texts.join('; ')
    }

    const getValue = (val: any) => {
      if (val && typeof val === 'object' && 'value' in val) {
        return val.value
      }
      return val
    }

    const handleNext = async () => {
      const hasRequiredForm = currentPageData.elements?.some(
        (el: any) => el.type === ElementType.Form && el.required === true
      )
      if (hasRequiredForm) {
        const invalidIds: string[] = []
        currentPageData.elements?.forEach((el: any) => {
          if (el.type !== ElementType.Form) return
          if (el.required !== true) return
          const keyA = typeof el.fieldId === 'string' && el.fieldId.trim() ? el.fieldId.trim() : ''
          const keyB = String(el.id)
          const rawV = (formValues[keyA] ?? formValues[keyB] ?? '')
          const v = getValue(rawV)
          if (String(v).trim() === '') {
            if (keyA) invalidIds.push(keyA)
            invalidIds.push(keyB)
          }
        })

        if (invalidIds.length > 0) {
          setInvalidFields(invalidIds)
          setValidationError(config.validation?.requiredMessage || 'Preencha os campos obrigatórios antes de continuar.')
          return
        }
      }

      const gm = config.gamification || {}
      const hdr = config.header || {}
      const onCover = hdr.hideOnFirstPage === true && currentStep === 0
      const wasAnswered = awardedSteps.has(currentStep)
      const pointsToAward = currentPageData?.scoreConfig?.enabled ? (currentPageData.scoreConfig.points || 0) : 0

      if (gm.coinsEnabled && !wasAnswered && !onCover && pointsToAward > 0) {
        logger.log('💰 [QuizViewer] Awarding points on Next:', pointsToAward)
        playCoin()
        const t = document.querySelector('[data-coin-target="true"][data-coin-context="viewer"]') as HTMLElement | null
        if (t) {
          const r = t.getBoundingClientRect()
          const centerX = window.innerWidth / 2
          const centerY = window.innerHeight / 2
          const targetX = r.left + r.width / 2
          const targetY = r.top + r.height / 2
          const dx = targetX - centerX
          const dy = targetY - centerY
          setCoinDx(dx)
          setCoinDy(dy)
          setCoinKey(prev => prev + 1)
        }
        setCoinAward(pointsToAward)
        setCoinAnimOn(true)

        const newStepsSet = new Set(awardedSteps).add(currentStep)
        setAwardedSteps(newStepsSet)

        if (currentPageData?.scorePopupConfig?.enabled) {
          setPopupContent({
            text: currentPageData.scorePopupConfig.text || 'Foi adicionado ao seu saldo!',
            footer: currentPageData.scorePopupConfig.footer || 'Continue para ganhar mais! 🚀'
          });
          setShowScorePopup(true)
        }

        setTimeout(() => {
          setCoins(prev => {
            const newScore = prev + pointsToAward
            saveScore(newScore, Array.from(newStepsSet) as number[])
            return newScore
          })
        }, 950)

        setTimeout(() => setCoinAnimOn(false), 1100)
      }

      await saveLeads()

      const hasConfetti = currentPageData?.confettiConfig?.enabled
      if (hasConfetti) {
        triggerConfetti()
        const timeoutId = window.setTimeout(() => {
          nextStep()
        }, 500)
        confettiTimeoutsRef.current.push(timeoutId)
      } else {
        nextStep()
      }
    }

    const handleButtonAction = async (action: 'next' | 'redirect', redirectUrl?: string, options?: { target?: '_self' | '_blank' }) => {
      logger.log('🎯 [QuizViewer] Ação de botão:', { action, redirectUrl, currentStep })
      saveLeads()

      if (action === 'redirect' && redirectUrl) {
        if (isRedirectingRef.current) return
        setIsRedirecting(true)
        isRedirectingRef.current = true

        // Rastrear clique externo (conversão)
        await trackExternalClick()

        await completeQuiz()



        // Injetar UTMs na URL de destino
        const finalUrl = addUTMsToRedirectUrl(redirectUrl)
        logger.log('🔗 [QuizViewer] Redirecionando com UTMs:', finalUrl)

        const target = options?.target || '_self'
        if (target === '_blank') {
          window.open(finalUrl, '_blank')
          setIsRedirecting(false)
          isRedirectingRef.current = false
        } else {
          window.location.href = finalUrl
        }
      } else if (action === 'next') {
        handleNext()
      }
    }

    const handleAnswer = async (answer: string | string[]) => {
      const customId = getQuestionId(currentPageData)
      const internalId = currentPageData.id.toString()
      saveLeads()
      const optionTexts = getOptionTexts(answer)
      const answerData = {
        value: optionTexts,
        optionIds: answer,
        customId: customId !== internalId ? customId : undefined,
        internalId: internalId
      }
      submitAnswer(customId, answerData)

      const hasConfetti = currentPageData.confettiConfig?.enabled
      if (hasConfetti) {
        triggerConfetti()
      }

      const gm = config.gamification || {}
      const hdr = config.header || {}
      const onCover = hdr.hideOnFirstPage === true && currentStep === 0
      const wasAnswered = answers[customId] !== undefined
      const pointsToAward = currentPageData.scoreConfig?.enabled ? (currentPageData.scoreConfig.points || 0) : 0

      logger.log('🎮 [QuizViewer] Gamification Check:', {
        coinsEnabled: gm.coinsEnabled,
        wasAnswered,
        onCover,
        stepAwarded: awardedSteps.has(currentStep),
        pointsToAward,
        scoreConfig: currentPageData.scoreConfig,
        currentStep,
        customId
      })

      if (gm.coinsEnabled && !wasAnswered && !onCover && !awardedSteps.has(currentStep) && pointsToAward > 0) {
        logger.log('💰 [QuizViewer] Awarding points:', pointsToAward)
        playCoin()
        const t = document.querySelector('[data-coin-target="true"][data-coin-context="viewer"]') as HTMLElement | null
        if (t) {
          const r = t.getBoundingClientRect()
          const centerX = window.innerWidth / 2
          const centerY = window.innerHeight / 2
          const targetX = r.left + r.width / 2
          const targetY = r.top + r.height / 2
          const dx = targetX - centerX
          const dy = targetY - centerY
          setCoinDx(dx)
          setCoinDy(dy)
          setCoinKey(prev => prev + 1)
        }
        setCoinAward(pointsToAward)
        setCoinAnimOn(true)

        const newStepsSet = new Set(awardedSteps).add(currentStep)
        setAwardedSteps(newStepsSet)

        if (currentPageData.scorePopupConfig?.enabled) {
          setPopupContent({
            text: currentPageData.scorePopupConfig.text || 'Foi adicionado ao seu saldo!',
            footer: currentPageData.scorePopupConfig.footer || 'Continue para ganhar mais! 🚀'
          });
          setShowScorePopup(true)
        }

        setTimeout(() => {
          setCoins(prev => {
            const newScore = prev + pointsToAward
            saveScore(newScore, Array.from(newStepsSet) as number[])
            return newScore
          })
        }, 950)

        setTimeout(() => setCoinAnimOn(false), 1100)
      }

      if (!isMultipleChoice) {
        const hasRequiredForm = currentPageData.elements?.some(
          (el: any) => el.type === ElementType.Form && el.required === true
        )
        if (hasRequiredForm) {
          const invalidIds: string[] = []
          currentPageData.elements?.forEach((el: any) => {
            if (el.type !== ElementType.Form) return
            if (el.required !== true) return
            const keyA = typeof el.fieldId === 'string' && el.fieldId.trim() ? el.fieldId.trim() : ''
            const keyB = String(el.id)
            const rawV = (formValues[keyA] ?? formValues[keyB] ?? '')
            const v = getValue(rawV)
            if (String(v).trim() === '') {
              if (keyA) invalidIds.push(keyA)
              invalidIds.push(keyB)
            }
          })
          if (invalidIds.length > 0) {
            setInvalidFields(invalidIds)
            setValidationError(config.validation?.requiredMessage || 'Preencha os campos obrigatórios antes de continuar.')
            return
          }
        }
        const delay = hasConfetti ? 500 : 500
        setTimeout(() => nextStep(), delay)
      }
    }

    const customId = getQuestionId(currentPageData)
    const currentAnswer = answers[customId]
    const selectedOption = (currentAnswer && typeof currentAnswer === 'object' && 'optionIds' in currentAnswer)
      ? currentAnswer.optionIds
      : (currentAnswer && typeof currentAnswer === 'object' && 'value' in currentAnswer)
        ? currentAnswer.value
        : currentAnswer



    return (
      <PageRenderer
        key={currentStep}
        page={currentPageData}
        config={config}
        onOptionSelect={handleAnswer}
        selectedOption={selectedOption}
        multiple={isMultipleChoice}
        onNext={handleNext}
        onFormChange={setFormValues}
        formValues={formValues}
        invalidFieldIds={invalidFields}
        validationMessage={validationError || undefined}
        onButtonAction={handleButtonAction}
        isRedirecting={isRedirecting}
        priceCheckboxes={priceCheckboxes}
        onPriceCheckboxChange={(id, checked) => setPriceCheckboxes(prev => ({ ...prev, [id]: checked }))}
        coins={coins}
        onConfetti={() => triggerConfetti('exit')}
        previewMode={true}
        globalFormValues={formValues}
        ignoreTimers={isRecoveredCompleted}
      />
    )
  }

  const totalSteps = quiz?.quiz_data?.pages?.length || 0
  const alignClass = config.contentAlignment === 'left' ? 'items-start' : config.contentAlignment === 'right' ? 'items-end' : 'items-center'
  const contentWidthClass = config.contentWidth === 'full'
    ? 'max-w-full'
    : config.contentWidth === 'large'
      ? 'max-w-4xl'
      : config.contentWidth === 'medium'
        ? 'max-w-2xl'
        : 'max-w-lg'

  const isLastPage = currentStep >= totalSteps - 1
  const isBoxed = config.containerStyle === 'boxed'



  const boxRadius = config.borderRadius === 'heavy' ? '1.5rem'
    : config.borderRadius === 'medium' ? '1rem'
      : config.borderRadius === 'light' ? '0.5rem'
        : '0'

  return (
    <>
      <SEOUpdater
        title={config.seoTitle}
        description={config.seoDescription}
        headScript={config.scripts?.head}
        footerScript={config.scripts?.footer}
      />
      <div
        className="min-h-screen flex flex-col"
        style={{
          backgroundColor: isBoxed ? '#f1f5f9' : config.colors.background,
          fontFamily: config.fontFamily
        }}
      >
        <QuizHeader
          currentStep={currentStep}
          totalSteps={totalSteps}
          config={config}
          onBack={currentStep > 0 && quiz?.quiz_data?.pages?.[currentStep]?.hideBackButton !== true ? prevStep : undefined}
          coins={coins}
          coinContext="viewer"
        />

        {/* Cover mode logic: reduce top padding on mobile when on first page in cover mode */}
        {(() => {
          const coverMode = config.header?.hideOnFirstPage === true
          const onCover = coverMode && currentStep === 0
          const hasLogo = !!config.header?.logoUrl
          // On mobile cover mode: no logo = pt-2, has logo = pt-0; else normal py-4
          const paddingClass = onCover
            ? (hasLogo ? 'px-4 pt-0 pb-4 md:py-4' : 'px-4 pt-2 pb-4 md:py-4')
            : 'px-4 py-4'
          return (
            <div className={`flex-grow flex flex-col ${config.contentAlignment === 'center' ? 'justify-center' : 'justify-start'} ${paddingClass} ${alignClass}`}>
              <div className={`w-full ${contentWidthClass}`}>
                {isBoxed ? (
                  <div
                    className="bg-white shadow-xl overflow-hidden"
                    style={{
                      borderRadius: boxRadius,
                      backgroundColor: config.colors.background
                    }}
                  >
                    <div className="p-6 md:p-8">
                      {renderCurrentPage()}
                    </div>
                  </div>
                ) : (
                  renderCurrentPage()
                )}
              </div>
            </div>
          )
        })()}

        {/* Confetti Layer */}
        {
          confettiOn && confettiParticles && (
            <div className="pointer-events-none fixed inset-0 z-[100]">
              {(() => {
                const { particles, style } = confettiParticles
                const borderRadius = style === 'circles' || style === 'fireworks' ? '50%' : style === 'stars' ? '0' : '2px'
                const clipPath = style === 'stars' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' : undefined

                return (
                  <>
                    {particles.map((p, i) => (
                      <span
                        key={i}
                        className="absolute"
                        style={{
                          left: p.startX,
                          top: p.startY,
                          width: `${p.size}px`,
                          height: `${p.size * (style === 'stars' ? 1 : 1.4)}px`,
                          backgroundColor: p.bg,
                          borderRadius: borderRadius,
                          clipPath: clipPath,
                          transform: 'translate(-50%, -50%)',
                          animation: `burst-${i} ${p.duration}ms ease-out ${p.delay}ms forwards`
                        }}
                      />
                    ))}
                    <style>{`
                    ${particles.map((p, i) =>
                      `@keyframes burst-${i}{0%{transform:translate(-50%,-50%) scale(0.6) rotate(${p.startRot}deg);opacity:1}50%{opacity:1}100%{transform:translate(calc(-50% + ${p.dx}px), calc(-50% + ${p.dy}px)) rotate(${p.endRot}deg) scale(1);opacity:0}}`
                    ).join('')}
                  `}</style>
                  </>
                )
              })()}
            </div>
          )
        }

        {/* Coin Animation Layer */}
        {
          coinAnimOn && (
            <div className="pointer-events-none fixed inset-0 z-[120] flex items-center justify-center">
              {config.gamification?.animationType === 'simple' ? (
                <div
                  key={coinKey}
                  className="absolute"
                  style={{
                    left: `calc(50% + ${coinDx}px)`,
                    top: `calc(50% + ${coinDy}px)`,
                    transform: 'translate(-50%, -50%)',
                    animation: 'coin-pop-simple 1s ease-out forwards'
                  }}
                >
                  <div
                    className="text-2xl font-bold"
                    style={{
                      color: config.gamification?.style === 'money' ? '#15803d' : '#b45309',
                      textShadow: '0 1px 0 #fff'
                    }}
                  >
                    +{coinAward}
                  </div>
                </div>
              ) : (
                <div
                  key={coinKey}
                  className="relative flex items-center justify-center"
                  style={{
                    animation: 'coin-fly 1s ease-in-out forwards'
                  }}
                >
                  <div className="text-4xl filter drop-shadow-lg">
                    {config.gamification?.style === 'money' ? '💵' : '🪙'}
                  </div>
                  <div
                    className="absolute top-0 right-0 -mt-2 -mr-2 text-xl font-bold"
                    style={{
                      color: config.gamification?.style === 'money' ? '#15803d' : '#b45309',
                      textShadow: '0 1px 0 #fff'
                    }}
                  >
                    +{coinAward}
                  </div>
                </div>
              )}
              <style>{`
              @keyframes coin-fly {
                0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
                20% { transform: translate(0, 0) scale(1.2); opacity: 1; }
                40% { transform: translate(0, 0) scale(1); opacity: 1; }
                80% { transform: translate(${coinDx}px, ${coinDy}px) scale(0.5); opacity: 1; }
                100% { transform: translate(${coinDx}px, ${coinDy}px) scale(0); opacity: 0; }
              }
              @keyframes coin-pop-simple {
                0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
                20% { transform: translate(-50%, -50%) scale(1.5); opacity: 1; }
                80% { transform: translate(-50%, -150%) scale(1); opacity: 1; }
                100% { transform: translate(-50%, -200%) scale(0.5); opacity: 0; }
              }
            `}</style>
            </div>
          )
        }

        {/* Score Popup */}
        {
          showScorePopup && (
            <ScorePopup
              points={coinAward}
              text={popupContent.text}
              footer={popupContent.footer}
              onClose={() => setShowScorePopup(false)}
              style={config.gamification?.style}
              preText={config.gamification?.style === 'money' ? (config.gamification?.currencySymbol ?? 'US$ ') : ''}
            />
          )
        }
      </div>
    </>
  )
}

export default QuizViewer